import React, { useState, useEffect } from 'react';
import {
  TabSection,
  SectionTitle,
  SectionDescription,
  UserList,
  UserCard,
  UserInfo,
  UserDetails,
  UserName,
  UserEmail,
  ActionButton,
  LoadingSpinner,
  EmptyState
} from '../../../pages/admin/components/styled';
import GlobalUsersTab from './GlobalUsersTab';
import { 
  getAllCompanies, 
  getCompanyUserCount, 
  FirestoreCompany 
} from '../../../services/creditService';

interface Company extends FirestoreCompany {
  userCount: number;
  subscriptionPlan: string;
  status: 'active' | 'inactive' | 'suspended';
  lastActivity: Date;
}

const AllCompaniesTab: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load companies from Firestore
  useEffect(() => {
    const loadCompanies = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get all companies from Firestore
        const firestoreCompanies = await getAllCompanies();
        
        // Enrich companies with additional data
        const enrichedCompanies = await Promise.all(
          firestoreCompanies.map(async (company) => {
            const userCount = await getCompanyUserCount(company.id);
            
            return {
              ...company,
              userCount,
              subscriptionPlan: company.domain === 'intuitionai.co' ? 'enterprise' : 'free',
              status: 'active' as const,
              lastActivity: new Date(), // In real app, this would come from user activity tracking
              createdAt: company.createdAt?.toDate ? company.createdAt.toDate() : new Date(company.createdAt),
              updatedAt: company.updatedAt?.toDate ? company.updatedAt.toDate() : new Date(company.updatedAt)
            };
          })
        );

        setCompanies(enrichedCompanies);
      } catch (err) {
        console.error('Failed to load companies:', err);
        setError('Failed to load companies. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, []);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  // If a company is selected for user management, show the users view
  if (selectedCompanyId) {
    const selectedCompany = companies.find(c => c.id === selectedCompanyId);
    return (
      <TabSection>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <ActionButton 
            variant="secondary"
            onClick={() => setSelectedCompanyId(null)}
          >
            ← Back to Companies
          </ActionButton>
          <div>
            <SectionTitle>👥 Users for {selectedCompany?.name}</SectionTitle>
            <SectionDescription>
              Manage users for {selectedCompany?.domain}
            </SectionDescription>
          </div>
        </div>
        <GlobalUsersTab companyFilter={selectedCompanyId} />
      </TabSection>
    );
  }

  const getStatusColor = (status: Company['status']) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'inactive': return '#94a3b8';
      case 'suspended': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return '#6366f1';
      case 'pro': return '#8b5cf6';
      case 'basic': return '#06b6d4';
      default: return '#94a3b8';
    }
  };

  return (
    <TabSection>
      <SectionTitle>🏢 All Companies</SectionTitle>
      <SectionDescription>
        Manage all companies registered on the IntuitionAI platform. 
        Monitor usage, billing, and subscription status.
      </SectionDescription>

      {loading && (
        <LoadingSpinner>Loading companies...</LoadingSpinner>
      )}

      {error && (
        <EmptyState>
          <div style={{ color: '#ef4444', marginBottom: '16px' }}>❌ {error}</div>
        </EmptyState>
      )}

      {!loading && !error && (
        <>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div style={{ color: '#cbd5e1' }}>
              Total Companies: <strong>{companies.length}</strong>
            </div>
            <ActionButton>+ Add Company</ActionButton>
          </div>

          {companies.length === 0 ? (
            <EmptyState>
              <div style={{ marginBottom: '16px' }}>🏢</div>
              <div>No companies found</div>
              <div style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '8px' }}>
                Companies will appear here as users sign up through the platform.
              </div>
            </EmptyState>
          ) : (
            <UserList>
              {companies.map((company) => (
                <UserCard key={company.id} $isActive={company.status === 'active'}>
                  <UserInfo>
                    <UserDetails>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <UserName>{company.name}</UserName>
                        <div style={{
                          background: getStatusColor(company.status),
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {company.status}
                        </div>
                        <div style={{
                          background: getPlanBadgeColor(company.subscriptionPlan),
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {company.subscriptionPlan}
                        </div>
                      </div>
                      <UserEmail>{company.domain}</UserEmail>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '8px' }}>
                        <div>👥 {company.userCount} users | 💎 {company.creditBalance.toLocaleString()} credits</div>
                        <div style={{ marginTop: '4px' }}>
                          Created: {company.createdAt.toLocaleDateString()} | 
                          Last Active: {company.lastActivity.toLocaleDateString()}
                        </div>
                      </div>
                    </UserDetails>
                  </UserInfo>
                  
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                    <ActionButton variant="secondary">View Details</ActionButton>
                    <ActionButton variant="secondary">Manage Credits</ActionButton>
                    <ActionButton 
                      variant="secondary"
                      onClick={() => setSelectedCompanyId(company.id)}
                    >
                      Manage Users
                    </ActionButton>
                    <ActionButton variant="secondary">Billing</ActionButton>
                    {company.status === 'active' ? (
                      <ActionButton variant="secondary">Suspend</ActionButton>
                    ) : (
                      <ActionButton>Activate</ActionButton>
                    )}
                  </div>
                </UserCard>
              ))}
            </UserList>
          )}
        </>
      )}
    </TabSection>
  );
};

export default AllCompaniesTab;
